// components/SupabaseDataProvider.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useMutablePlasmicQueryData } from "@plasmicapp/react-web/lib/query";
import { DataProvider } from "@plasmicapp/react-web/lib/host";
import { useSupabaseFromContext } from "./SupabaseClientContext";
import { useRealtimeSubscription } from "./RealtimeProvider";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { isPlasmicProxy } from '../lib/isPlasmicProxy';
import { getProxyFingerprint } from "../lib/proxy-client"; // <--- Import this

const IS_CLIENT = typeof window !== "undefined";
const isPrepass = () => !IS_CLIENT && !!(globalThis as any).__ssrPrepassEnv;

function parseTablesFromSelect(query: string): string[] {
  const regex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b\s*\(/g;
  const matches = Array.from(query.matchAll(regex));
  const tables = new Set<string>();
  const blacklist = new Set(["count", "avg", "sum", "min", "max", "rank", "dense_rank", "row_number"]);
  for (const match of matches) {
    if (!blacklist.has(match[1].toLowerCase())) tables.add(match[1]);
  }
  return Array.from(tables);
}

interface SupabaseDataProviderProps {
  children: React.ReactNode;
  tableName: string;
  dataName?: string;
  selectQuery?: string;
  match?: string; 
  queryKey?: string | readonly unknown[];
  realtimeTriggerTables?: string[];
  alwaysClient?: boolean;
  onRealtimeTrigger?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

export function SupabaseDataProvider({
  children,
  tableName,
  queryKey: customKeyFromProp,
  dataName,
  selectQuery = "*",
  match = "",
  realtimeTriggerTables = [],
  alwaysClient = false,
  onRealtimeTrigger,
}: SupabaseDataProviderProps) {
  const supabase = useSupabaseFromContext();
  const [isMounted, setIsMounted] = useState(false);
  const [useDevProxy] = useState(() => isPlasmicProxy());

  useEffect(() => { setIsMounted(true); }, []);

  const providerId = dataName || tableName || "unknown";
  const inPrepass = isPrepass();

  // 1. Key Readiness
  const isKeyReady = useMemo(() => {
    if (!tableName) return false;
    return !tableName.includes("undefined") && !match.includes("undefined");
  }, [tableName, match]);

  // 2. Stable Cache Key
  const queryKeyToUse = useMemo(() => {
    if ((inPrepass && alwaysClient) || !isKeyReady) return null;
    const normalizedSelect = selectQuery.replace(/\s+/g, ""); 
    const baseKey = `/plasmictable/${tableName}/${normalizedSelect}/${match}`;
    // Append -devproxy only after mounting on client to avoid hydration mismatch
    return (useDevProxy && isMounted) ? `${baseKey}-devproxy` : baseKey;
  }, [tableName, selectQuery, match, isKeyReady, useDevProxy, isMounted, inPrepass, alwaysClient]);

  // 3. Fetcher
  const fetcher = useCallback(async () => {
    if (!queryKeyToUse || (inPrepass && alwaysClient)) return [];

    try {
      if (useDevProxy && IS_CLIENT) {
        // --- THIS WAITS FOR THE HANDSHAKE TO COMPLETE ---
        const fingerprint = await getProxyFingerprint(); 
        
        const resp = await fetch("/api/plasmic-studio-proxy", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-proxy-fingerprint": fingerprint || '' 
          },
          body: JSON.stringify({ 
            operation: "select", 
            table: tableName, 
            select: selectQuery, 
            matchString: match 

          }),
        });
        if (!resp.ok) throw new Error(`Proxy fetch failed: ${resp.status}`);
        return (await resp.json()) || [];
      } 
      
      // Direct Supabase Fetch
      const baseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      const { data: { session } } = await supabase.auth.getSession();
      
      const url = new URL(`${baseUrl}/rest/v1/${tableName.split('?')[0]}`);
      url.searchParams.set('select', selectQuery);
      const rawFilterString = [tableName.split('?')[1], match].filter(Boolean).join('&');
      const finalUrl = url.toString() + (rawFilterString ? `&${rawFilterString}` : "");

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'apikey': anonKey!,
          'Content-Type': 'application/json',
          ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` })
        }
      });

      if (!response.ok) throw new Error('Fetch failed');
      const res = await response.json();
      if (inPrepass) console.log(`[Prepass] ✅ DONE: ${providerId} (${res?.length} rows)`);
      return res || [];
    } catch (e: any) {
      if (inPrepass) console.error(`[Prepass] 🛑 ERROR: ${providerId} -> ${e.message}`);
      throw e;
    }
  }, [tableName, selectQuery, match, supabase, useDevProxy, inPrepass, queryKeyToUse, providerId, alwaysClient]);

  // 4. Hook
  const { data } = useMutablePlasmicQueryData(customKeyFromProp || queryKeyToUse, fetcher, {
    revalidateOnMount: alwaysClient ? true : (useDevProxy ? true : undefined),
    revalidateIfStale: alwaysClient ? true : useDevProxy,
  });

  if (inPrepass && data === undefined && queryKeyToUse && !alwaysClient) {
    throw fetcher(); 
  }

  // 5. Realtime
  const tablesToSubscribe = useMemo(() => {
    const cleanTableName = tableName?.split("?")[0];
    const extractedTables = parseTablesFromSelect(selectQuery);
    let tables = realtimeTriggerTables.length > 0 
        ? [cleanTableName, ...realtimeTriggerTables] 
        : [cleanTableName, ...extractedTables];
    return Array.from(new Set(tables)).filter(Boolean).filter((t) => !t.includes('?') && t !== "phones").sort() as string[];
  }, [tableName, selectQuery, realtimeTriggerTables]);

  useRealtimeSubscription(tablesToSubscribe, onRealtimeTrigger);

  const is_loaded = data !== undefined;

  return (
    <DataProvider name={providerId} data={data || []}>
      <DataProvider name="is_loaded" data={is_loaded}>
        {children}
      </DataProvider>
    </DataProvider>
  );
}
